/* Upload steps — full modal wizard (Upload File → Data Mapping → Review Errors)
   modelled on the real MarketOS PO-details "Add by Upload File" modal.
   On proceed, the import runs in-place in the main step area. */
import React, { useState as uState, useEffect as uEffect, useRef as uRef } from 'react';
import { Icons as Ico } from './icons.jsx';
import { StepFooter } from './stepComponents.jsx';
import BulkProductSkuUpload from '../../containers/inventory/BulkProductSkuUpload';
import InitialInventory from '../../containers/warehouse-location/initial-inventory';

/* ---------- custom mapping dropdown (matches ReactSelect look) ---------- */
function MapSelect({ value, options, onChange, status, placeholder }) {
  const [open, setOpen] = uState(false);
  const ref = uRef(null);

  uEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const conflict = status && status.startsWith("conflict");
  const cls = ["map-select", value ? "filled" : "", status === "missing" ? "missing" : "", conflict ? "conflict" : ""]
    .filter(Boolean).join(" ");

  return (
    <div className="map-select-wrap" ref={ref}>
      <button type="button" className={cls} onClick={() => setOpen((o) => !o)}>
        <span className={value ? "ms-val" : "ms-placeholder"}>{value || placeholder || "Don't import"}</span>
        {value && status === "ok" && <Ico.Check size={15} className="map-ok" />}
        {conflict && <span className="map-conflict">Used by {status.split("-")[1]}</span>}
        <Ico.ChevronDown size={15} className="map-select-caret" />
      </button>
      {open && (
        <div className="map-menu">
          <button type="button" className={`map-opt ${!value ? "sel" : ""}`} onClick={() => { onChange(""); setOpen(false); }}>
            <span className="mo-muted">Don't import</span>
            {!value && <Ico.Check size={14} />}
          </button>
          {options.map((c) => (
            <button type="button" key={c} className={`map-opt ${value === c ? "sel" : ""}`} onClick={() => { onChange(c); setOpen(false); }}>
              <span>{c}</span>
              {value === c && <Ico.Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Upload wizard modal — 3 steps, like the PO "Add by Upload File"
   ============================================================ */
function UploadWizardModal({ cfg, onClose, onConfirm }) {
  const [step, setStep] = uState(0); // 0 upload · 1 mapping · 2 review
  const [uploaded, setUploaded] = uState(false);
  const [drag, setDrag] = uState(false);
  const [mapping, setMapping] = uState(() => {
    const m = {};
    cfg.fields.forEach((f) => { m[f.key] = f.auto || ""; });
    return m;
  });

  const setField = (key, val) => setMapping((m) => ({ ...m, [key]: val }));
  const takenBy = (col, exceptKey) => Object.entries(mapping).find(([k, v]) => v === col && k !== exceptKey);
  const fieldLabel = (key) => (cfg.fields.find((f) => f.key === key) || {}).label || key;

  const requiredUnmapped = cfg.fields.filter((f) => f.required && !mapping[f.key]);
  const validRows = cfg.totalRows - cfg.errorCount;

  const steps = [
    { n: 1, label: "Upload File" },
    { n: 2, label: "Data Mapping" },
    { n: 3, label: "Review Errors" },
  ];
  const headings = ["Upload File", "Data Mapping", "Review Errors"];

  const nextDisabled = (step === 0 && !uploaded) || (step === 1 && requiredUnmapped.length > 0);

  const onBack = () => {
    if (step === 2) { setUploaded(true); setStep(0); }   // "Re-Upload File"
    else if (step > 0) setStep(step - 1);
  };
  const onNext = () => {
    if (step < 2) setStep(step + 1);
    else onConfirm();                                     // "Discard Errors & Proceed"
  };

  return (
    <div className="dz-modal-overlay" onClick={onClose}>
      <div className="dz-modal" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="dz-modal-head">
          <span className="dzm-title">{cfg.modalTitle}</span>
          <button className="dzm-close" onClick={onClose} aria-label="Close"><Ico.X size={18} /></button>
        </div>

        {/* horizontal stepper */}
        <div className="dzm-steps">
          {steps.map((s, i) => {
            const state = i < step ? "done" : i === step ? "active" : "pending";
            return (
              <React.Fragment key={s.n}>
                <div className={`dzm-step ${state}`}>
                  <span className="dzm-step-num">{i < step ? <Ico.Check size={13} /> : s.n}</span>
                  <span className="dzm-step-txt">
                    <span className="dzm-step-k">Step {s.n}</span>
                    <span className="dzm-step-d">{s.label}</span>
                  </span>
                </div>
                {i < steps.length - 1 && <div className={`dzm-step-bar ${i < step ? "filled" : ""}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* body */}
        <div className="dzm-body">
          <div className="dzm-box">
            <div className="dzm-box-head">{headings[step]}</div>
            <div className="dzm-box-body">

              {/* STEP 0 — Upload file */}
              {step === 0 && (
                <React.Fragment>
                  {!uploaded ? (
                    <div
                      className={`dropzone in-modal ${drag ? "drag" : ""}`}
                      onClick={() => setUploaded(true)}
                      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={() => setDrag(false)}
                      onDrop={(e) => { e.preventDefault(); setDrag(false); setUploaded(true); }}
                    >
                      <div className="dz-icon"><Ico.Upload /></div>
                      <div className="dz-title">Drag &amp; drop your file, or <span className="browse">browse</span></div>
                      <div className="dz-hint">Accepts .xlsx or .csv · one file · up to 10,000 rows</div>
                      <button className="link-btn dz-sample" onClick={(e) => e.stopPropagation()}><Ico.Download size={15} /> Download sample file</button>
                    </div>
                  ) : (
                    <div className="upl-chip">
                      <div className="uc-icon"><Ico.FileSpreadsheet /></div>
                      <div className="uc-meta">
                        <div className="uc-name">{cfg.fileName}</div>
                        <div className="uc-sub">{cfg.totalRows.toLocaleString()} rows · {cfg.fileSize}</div>
                      </div>
                      <button className="icon-btn" onClick={() => setUploaded(false)} title="Remove"><Ico.Trash /></button>
                    </div>
                  )}
                </React.Fragment>
              )}

              {/* STEP 1 — Data mapping */}
              {step === 1 && (
                <React.Fragment>
                  <p className="dzm-lead">
                    We matched your file's columns to MarketOS fields. Adjust any that look off, then continue.
                  </p>
                  <div className="map-grid4">
                    {cfg.fields.map((f) => {
                      const val = mapping[f.key];
                      const conflict = val && takenBy(val, f.key);
                      const status = conflict ? `conflict-${fieldLabel(conflict[0])}` : f.required && !val ? "missing" : "ok";
                      return (
                        <div className="map-cell" key={f.key}>
                          <div className="mc-label">
                            {f.label}
                            {f.required ? <span className="mc-req">*</span> : <span className="mc-opt">(Optional)</span>}
                          </div>
                          <MapSelect
                            value={val}
                            options={cfg.fileColumns}
                            status={status}
                            placeholder={f.label}
                            onChange={(v) => setField(f.key, v)}
                          />
                          <div className="mc-sample">{(f.preview && f.preview[0]) || "—"}</div>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              )}

              {/* STEP 2 — Review errors */}
              {step === 2 && (
                <div className="err-grid">
                  <div className="err-grid-head">
                    <span>Row #</span>
                    <span>{cfg.errorIdLabel}</span>
                    <span>{cfg.errorValLabel || "Identifier"}</span>
                    <span>Error message</span>
                  </div>
                  <div className="err-grid-body">
                    {cfg.errorRows.length === 0 ? (
                      <div className="err-grid-empty">No Error Found!.</div>
                    ) : (
                      <React.Fragment>
                        {cfg.errorRows.map((r, i) => (
                          <div className="err-grid-row" key={i}>
                            <span className="er-row">{r.row}</span>
                            <span>{r.id}</span>
                            <span className="mono">{r.value}</span>
                            <span className="er-msg"><Ico.AlertTriangle size={14} /> {r.message}</span>
                          </div>
                        ))}
                        {cfg.errorCount > cfg.errorRows.length && (
                          <div className="err-grid-more">+ {cfg.errorCount - cfg.errorRows.length} more rows with similar issues</div>
                        )}
                      </React.Fragment>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* footer */}
        <div className="dzm-foot">
          <button className="btn-outline-modal" disabled={step === 0} onClick={onBack}>
            {step === 2 ? <React.Fragment><Ico.RotateCcw size={15} /> Re-Upload File</React.Fragment> : <React.Fragment><Ico.ArrowLeft /> Back</React.Fragment>}
          </button>
          <div className="dzm-foot-right">
            {step === 1 && requiredUnmapped.length > 0 && <span className="dzm-foot-hint">Map all required fields to continue</span>}
            <button className="btn-primary font-semibold" disabled={nextDisabled} onClick={onNext}>
              {step === 2 ? "Discard Error & Proceed" : <React.Fragment>Next <Ico.ArrowRight /></React.Fragment>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   In-place processing panel (shown in the main step area)
   ============================================================ */
function ProcessingPanel({ cfg, onDone }) {
  const [pct, setPct] = uState(0);
  const [stepIdx, setStepIdx] = uState(0);

  uEffect(() => {
    let p = 0;
    const id = setInterval(() => {
      p = Math.min(100, p + Math.random() * 7 + 3);
      setPct(Math.round(p));
      setStepIdx(Math.min(cfg.processingSteps.length - 1, Math.floor((p / 100) * cfg.processingSteps.length)));
      if (p >= 100) { clearInterval(id); setTimeout(onDone, 650); }
    }, 240);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="proc-panel">
      <div className="proc-top">
        <div className="proc-spinner"><Ico.RefreshCw /></div>
        <div>
          <div className="proc-title">{cfg.processingTitle}</div>
          <div className="proc-sub">{cfg.fileName} · {cfg.totalRows.toLocaleString()} rows</div>
        </div>
        <div className="proc-pct">{pct}%</div>
      </div>
      <div className="proc-bar"><div className="proc-bar-fill" style={{ width: `${pct}%` }} /></div>
      <div className="proc-steps">
        {cfg.processingSteps.map((s, i) => {
          const state = i < stepIdx ? "done" : i === stepIdx ? "active" : "pending";
          return (
            <div className={`proc-step ${state}`} key={s}>
              <span className="ps-dot">
                {state === "done" ? <Ico.Check size={12} /> : state === "active" ? <Ico.RefreshCw size={12} /> : null}
              </span>
              <span className="ps-label">{s}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Reusable upload step
   ============================================================ */
function UploadStep({ state, set, onNext, onBack, onSkip, backHidden, cfg }) {
  const done = !!state[cfg.doneKey];
  const [phase, setPhase] = uState(done ? "done" : "empty"); // empty | modal | processing | done
  const [realOpen, setRealOpen] = uState(false); // drives cfg.RealUpload when this step is wired to a real feature
  const RealUpload = cfg.RealUpload;

  // When a real upload component is wired in (e.g. step 2 → BulkProductSkuUpload), "Upload file"
  // opens that modal instead of the simulated wizard; its onSuccess marks this step done.
  const openModal = () => { if (RealUpload) { setRealOpen(true); return; } setPhase("modal"); };
  const startProcessing = () => setPhase("processing");
  const finishProcessing = () => { set({ [cfg.doneKey]: true }); setPhase("done"); };
  // Mark the step done on a real upload. We don't force the modal closed here: BulkProductSkuUpload
  // closes itself on "Done"; InitialInventory stays on its success screen until the user closes it via
  // its own × (which resets its internal wizard state).
  const onRealSuccess = () => { set({ [cfg.doneKey]: true }); setPhase("done"); };
  const remove = () => { set({ [cfg.doneKey]: false }); setPhase("empty"); };

  return (
    <div className="card wide">
      <div className="card-body">
        {/* heading */}
        <div className="card-head-row">
          <div className="chr-icon">{cfg.icon}</div>
          <div>
            <div className="step-tag">{cfg.stepTag}</div>
            <h2>{cfg.title}</h2>
            <p>{cfg.desc}</p>
          </div>
        </div>

        {/* export current data to start from / reference */}
        {cfg.onExport && (
          <div className="ob-export-row" style={{ marginBottom: "16px" }}>
            <button className="link-btn" onClick={cfg.onExport} disabled={cfg.exporting}>
              <Ico.Download size={15} /> {cfg.exporting ? "Exporting…" : cfg.exportLabel}
            </button>
          </div>
        )}

        {/* main body by phase */}
        {phase === "processing" ? (
          <ProcessingPanel cfg={cfg} onDone={finishProcessing} />
        ) : phase === "done" ? (
          <React.Fragment>
            <div className="import-success">
              <div className="is-icon"><Ico.CircleCheck size={26} /></div>
              <div className="is-text">
                <div className="is-title">{cfg.successTitle}</div>
                <div className="is-sub">{cfg.successSub}</div>
              </div>
            </div>
            <div className="file-card">
            <div className="file-head">
              <div className="fh-icon"><Ico.FileSpreadsheet /></div>
              <div>
                <div className="fh-name">{cfg.fileName}</div>
                <div className="fh-meta">{cfg.fileSize}</div>
              </div>
              <button className="link-btn replace-file" onClick={remove}><Ico.RotateCcw size={15} /> Replace file</button>
            </div>
          </div>
          </React.Fragment>
        ) : (
          /* empty — launch card */
          <div className="upl-launch">
            <div className="ul-drop" onClick={openModal}>
              <div className="dz-icon"><Ico.Upload /></div>
              <div className="ul-text">
                <div className="ul-title">Upload one file to get started</div>
                <div className="ul-sub">We'll walk you through mapping columns and fixing any errors.</div>
              </div>
              <button className="btn-primary font-semibold" onClick={(e) => { e.stopPropagation(); openModal(); }}>
                <Ico.Upload /> Upload file
              </button>
            </div>
          </div>
        )}

        {phase === "done" && cfg.note && (
          <div className="info-box lined" style={{ marginTop: "22px" }}>
            <div className="info-row">
              <Ico.Info />
              <div><div className="it-body">{cfg.note}</div></div>
            </div>
          </div>
        )}

        <StepFooter
          onBack={onBack}
          backHidden={backHidden}
          onSkip={phase === "processing" || phase === "done" ? undefined : onSkip}
          onNext={onNext}
          nextDisabled={phase === "processing" || !done}
          nextLabel={cfg.nextLabel}
        />
      </div>

      {phase === "modal" && !RealUpload && (
        <UploadWizardModal cfg={cfg} onClose={() => setPhase("empty")} onConfirm={startProcessing} />
      )}
      {RealUpload && (
        <RealUpload open={realOpen} setOpen={setRealOpen} hideTrigger onSuccess={onRealSuccess} />
      )}
    </div>
  );
}

/* ---------- Map products (catalog · step 1 of 2) ---------- */
/* Adapter: the onboarding product-sku modal pre-fills its "Download Sample File" with the user's SKUs
   (prefillSampleWithSkus). Same {open,setOpen,hideTrigger,onSuccess} contract UploadStep drives. */
function ProductSkuUploadModal({ open, setOpen, hideTrigger, onSuccess }) {
  return (
    <BulkProductSkuUpload
      open={open}
      setOpen={setOpen}
      hideTrigger={hideTrigger}
      onSuccess={onSuccess}
      prefillSampleWithSkus
    />
  );
}

function MapProductsStep({ soloStep, ...props }) {
  // soloStep: sellers (role 2) have no inventory step, so mapping is the only setup step before finish.
  const cfg = {
    doneKey: "mappingUploaded",
    // No separate "Export all SKUs" button here — the modal's "Download Sample File" generates the
    // product-sku template pre-filled with the user's SKUs (see ProductSkuUploadModal below).
    RealUpload: ProductSkuUploadModal, // step 1 opens the real product-sku single-file upload modal
    stepTag: soloStep ? "Catalog setup" : "Catalog setup · Step 1 of 2",
    icon: <Ico.Package />,
    title: "Map SKUs, create products & vendor items",
    desc: "Upload one file and we'll map your SKUs, create product records and link vendor items — all at once.",
    modalTitle: "Add SKUs, products & vendor items by file upload",
    features: [
      { icon: <Ico.Tag />, title: "Map your SKUs", text: "Match each row to a SKU" },
      { icon: <Ico.Package />, title: "Create products", text: "Build product records" },
      { icon: <Ico.Link />, title: "Link vendor items", text: "Connect vendor item #s" },
    ],
    templateName: "sku-product-vendor-mapping.xlsx",
    fileName: "product-mapping.xlsx",
    fileSize: "86 KB",
    totalRows: 412,
    fileColumns: ["SKU", "ASIN", "Product Title", "Vendor Name", "Vendor Item", "UPC", "Cost"],
    fields: [
      { key: "sku", label: "SKU", required: true, auto: "SKU", preview: ["MOS-1042-BLK", "MOS-2210-WHT", "MOS-3387-GRY"] },
      { key: "identifier", label: "Product ID (ASIN)", required: true, auto: "ASIN", preview: ["B07QXMNZ4P", "B08LMR3K9T", "B09FTW2X1L"] },
      { key: "title", label: "Product Title", required: true, auto: "Product Title", preview: ["Aurora Desk Lamp — Black", "Nimbus Air Purifier", "Terra Ceramic Planter"] },
      { key: "vendor", label: "Vendor", required: true, auto: "Vendor Name", preview: ["Lumière Co.", "CleanAir Mfg.", "GreenLeaf"] },
      { key: "vendorItem", label: "Vendor Item #", required: false, auto: "Vendor Item", preview: ["LC-DL-0042", "CA-AP-2210", "GL-PLT-08"] },
      { key: "upc", label: "UPC", required: false, auto: "UPC", preview: ["890123456789", "812345600012", "799champ"] },
    ],
    errorIdLabel: "Identifier type",
    errorValLabel: "Identifier",
    errorCount: 16,
    errorRows: [
      { row: 38, id: "ASIN", value: "B09FT2X1", message: "ASIN must be exactly 10 characters" },
      { row: 104, id: "UPC", value: "89382-112", message: "UPC must be 12 digits" },
      { row: 152, id: "Vendor", value: "—", message: "Vendor name is blank" },
      { row: 261, id: "ASIN", value: "b07qxmnz4p", message: "ASIN must be uppercase" },
      { row: 333, id: "UPC", value: "8.9382E+11", message: "UPC stored as scientific notation" },
    ],
    parseStats: [
      { num: "412", lbl: "Rows found", tone: "neutral" },
      { num: "396", lbl: "Mapped", tone: "good" },
      { num: "16", lbl: "Needs review", tone: "warn" },
    ],
    tableHead: ["SKU", "ASIN", "Product", "Vendor", "Vendor Item #"],
    mono: [0, 1, 4],
    tableRows: [
      ["MOS-1042-BLK", "B07QXMNZ4P", "Aurora Desk Lamp — Black", "Lumière Co.", "LC-DL-0042"],
      ["MOS-2210-WHT", "B08LMR3K9T", "Nimbus Air Purifier", "CleanAir Mfg.", "CA-AP-2210"],
      ["MOS-3387-GRY", "B09FTW2X1L", "Terra Ceramic Planter, 8\"", "GreenLeaf", "GL-PLT-08"],
      ["MOS-4501-NVY", "B07ZZK4M8D", "Coastal Throw Blanket", "Hearth & Home", "HH-TB-451"],
    ],
    processingTitle: "Mapping your catalog",
    processingSteps: ["Reading file", "Mapping SKUs", "Creating products", "Linking vendor items"],
    successTitle: "Catalog mapped successfully",
    successSub: "396 of 412 rows imported — your SKUs, products and vendor items are ready.",
    nextLabel: soloStep ? "Finish setup" : "Continue",
  };
  return <UploadStep {...props} cfg={cfg} />;
}

/* Adapter: UploadStep drives RealUpload via {open,setOpen,hideTrigger,onSuccess}; InitialInventory uses
   {addProduct,setAddProduct,refreshGridClick} + our onUploaded hook. Closing it (its own × → clearStates)
   resets its internal wizard state. */
function InitialInventoryModal({ open, setOpen, onSuccess }) {
  return (
    <InitialInventory
      addProduct={open}
      setAddProduct={setOpen}
      refreshGridClick={() => {}}
      onUploaded={onSuccess}
      embedded
      prefillSampleWithIdentifiers
    />
  );
}

/* ---------- Warehouse inventory (catalog · step 2 of 2) ---------- */
function WarehouseInventoryStep(props) {
  const cfg = {
    doneKey: "inventoryUploaded",
    // No separate "Export all products" button here — the modal's "Download Sample File" generates the
    // initial-inventory template pre-filled with the user's product identifiers (see InitialInventoryModal).
    RealUpload: InitialInventoryModal, // step 2 opens the real Initial Inventory upload modal
    stepTag: "Catalog setup · Step 2 of 2",
    icon: <Ico.Warehouse />,
    title: "Upload your warehouse inventory",
    desc: "Upload one file with on-hand quantity and unit cost — we'll value your inventory from day one.",
    modalTitle: "Add warehouse inventory by file upload",
    features: [
      { icon: <Ico.Boxes />, title: "On-hand stock", text: "Quantity per SKU" },
      { icon: <Ico.DollarSign />, title: "Unit costs", text: "For inventory value" },
      { icon: <Ico.Warehouse />, title: "By location", text: "Across warehouses" },
    ],
    templateName: "warehouse-inventory.xlsx",
    fileName: "warehouse-inventory.xlsx",
    fileSize: "72 KB",
    totalRows: 388,
    fileColumns: ["SKU", "Warehouse", "On Hand Qty", "Unit Cost", "Bin"],
    fields: [
      { key: "sku", label: "SKU", required: true, auto: "SKU", preview: ["MOS-1042-BLK", "MOS-2210-WHT", "MOS-3387-GRY"] },
      { key: "warehouse", label: "Warehouse", required: true, auto: "Warehouse", preview: ["Dallas — TX1", "Dallas — TX1", "Reno — NV2"] },
      { key: "qty", label: "Quantity", required: true, auto: "On Hand Qty", preview: ["420", "188", "640"] },
      { key: "cost", label: "Unit Cost", required: true, auto: "Unit Cost", preview: ["$8.40", "$23.10", "$4.75"] },
      { key: "bin", label: "Bin / Location", required: false, auto: "Bin", preview: ["A-12-3", "A-04-1", "C-22-7"] },
    ],
    errorIdLabel: "SKU",
    errorValLabel: "Value",
    errorCount: 7,
    errorRows: [
      { row: 51, id: "MOS-7781-RED", value: "-12", message: "Quantity can't be negative" },
      { row: 119, id: "MOS-8120-GRN", value: "$—", message: "Unit cost is blank" },
      { row: 204, id: "MOS-5567-BLU", value: "abc", message: "Quantity must be a number" },
    ],
    parseStats: [
      { num: "388", lbl: "SKUs", tone: "neutral" },
      { num: "14,920", lbl: "Total units", tone: "good" },
      { num: "$182,430", lbl: "Inventory value", tone: "neutral" },
    ],
    tableHead: ["SKU", "Warehouse", "Quantity", "Unit Cost"],
    mono: [0],
    tableRows: [
      ["MOS-1042-BLK", "Dallas — TX1", "420", "$8.40"],
      ["MOS-2210-WHT", "Dallas — TX1", "188", "$23.10"],
      ["MOS-3387-GRY", "Reno — NV2", "640", "$4.75"],
      ["MOS-4501-NVY", "Reno — NV2", "212", "$11.90"],
    ],
    processingTitle: "Importing your inventory",
    processingSteps: ["Reading file", "Matching SKUs", "Valuing stock", "Saving inventory"],
    successTitle: "Inventory imported successfully",
    successSub: "381 of 388 rows imported — your on-hand stock is valued and ready.",
    note: "Costs are used for inventory valuation and profit reporting. You can update them anytime from Inventory settings.",
    nextLabel: "Finish setup",
  };
  return <UploadStep {...props} cfg={cfg} />;
}

/* ---------- Done (standalone, unused by the split flows but kept) ---------- */
function DoneStep({ state, onEnter }) {
  const items = [
    { ok: !!state.sellerType, label: state.sellerType === "new" ? "New Amazon seller profile" : "Existing seller profile" },
    { ok: state.connected, label: state.connected ? "Amazon account connected" : "Amazon connection skipped" },
    { ok: !!state.skus, label: state.skus ? `${state.skus.total} SKUs imported` : "SKU import skipped" },
    { ok: state.mappingUploaded, label: state.mappingUploaded ? "Product & vendor mapping uploaded" : "Mapping skipped" },
    { ok: state.inventoryUploaded, label: state.inventoryUploaded ? "Warehouse inventory uploaded" : "Inventory skipped" },
  ];
  return (
    <div className="max-w-3xl mx-auto">
      <div className="onboarding-card text-center">
        <div className="mb-8">
          <Ico.CircleCheck size={80} style={{ color: "var(--onboarding-success)", margin: "0 auto" }} className="animate-pulse-success" />
        </div>
        <h2 className="text-3xl font-bold text-foreground mb-3">You're all set up!</h2>
        <p className="text-muted-foreground font-medium text-lg mb-8">
          Your catalog and inventory are ready. You can finish any skipped steps anytime from your dashboard.
        </p>
        <div className="bg-success/10 border-success/20 border rounded-lg p-6 mb-8" style={{ textAlign: "left", maxWidth: "460px", margin: "0 auto 32px" }}>
          {items.map((it, i) => (
            <div key={i} className="flex items-center" style={{ marginBottom: i < items.length - 1 ? "12px" : 0 }}>
              {it.ok
                ? <Ico.CircleCheck size={20} style={{ color: "var(--onboarding-success)", marginRight: "12px", flexShrink: 0 }} />
                : <Ico.Clock size={20} style={{ color: "var(--onboarding-muted-foreground)", marginRight: "12px", flexShrink: 0 }} />}
              <span style={{ fontSize: "14px", fontWeight: 600, color: it.ok ? "var(--onboarding-foreground)" : "var(--onboarding-muted-foreground)" }}>
                {it.label}
              </span>
            </div>
          ))}
        </div>
        <button onClick={onEnter} className="btn-primary font-semibold" style={{ height: "44px", padding: "8px 28px" }}>
          Enter MarketOS <Ico.ArrowRight />
        </button>
      </div>
    </div>
  );
}

export { UploadStep, MapProductsStep, WarehouseInventoryStep, DoneStep, UploadWizardModal, ProcessingPanel };
